import { useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import {
  AlertTriangle,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  DollarSign,
  Heart,
  Info,
  TestTube2,
  Timer,
  Wrench,
  X,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getCollectionLabel,
  getRelatedBlocks,
  type ContentBlock,
  type ProcedureBody,
  type TestBody,
} from "@src/packages/content";
import { useLocale, useTranslations } from "@src/packages/i18n";
import type { ChatStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { IconButton, SectionTitle } from "@louise/moon-ui";
import FactSheetHeader from "../../components/FactSheetHeader";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import { EMBER } from "../../../onboarding/theme";
import testTubesIllustration from "../../../assets/images/you-icons/test-tubes.png";

type Props = NativeStackScreenProps<ChatStackParamList, "ProcedureDetail">;
type BiopsyTab = "overview" | "procedure" | "testing";

function BodyCopy({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{ fontSize: 14, lineHeight: 22, color: EMBER.colors.textLight }}
    >
      {children}
    </Text>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 8 }}>
      {items.map((item) => (
        <Text
          key={item}
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 20,
            color: EMBER.colors.textLight,
          }}
        >
          • {item}
        </Text>
      ))}
    </View>
  );
}

function CompactValueGrid({
  items,
}: {
  items: { label: string; value: string }[];
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {items.map((item) => (
        <View
          key={item.label}
          style={{
            width: "48%",
            minHeight: 68,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              fontWeight: "700",
              color: EMBER.colors.textFaint,
              textTransform: "uppercase",
            }}
          >
            {item.label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 5,
              fontSize: 13,
              lineHeight: 18,
              fontWeight: "600",
              color: EMBER.colors.textMid,
            }}
          >
            {item.value}
          </Text>
        </View>
      ))}
    </View>
  );
}

function TestCategoryCard({
  test,
  onPress,
}: {
  test: ContentBlock<"test">;
  onPress: () => void;
}) {
  const body: TestBody = test.body;
  const cost = body.costRange
    ? `$${body.costRange.low}–$${body.costRange.high}`
    : undefined;

  return (
    <View style={{ width: "48%" }}>
      <AccessGridCard
        title={test.title}
        description={test.subtitle}
        imageSource={testTubesIllustration}
        imageStyle={{ width: 66, height: 84 }}
        onPress={onPress}
        tags={
          <>
            <AccessGridCardPill
              label={getCollectionLabel(body)}
              icon={TestTube2}
            />
            {body.turnaroundTime ? (
              <AccessGridCardPill label={body.turnaroundTime} icon={Timer} />
            ) : null}
            {cost ? (
              <AccessGridCardPill label={cost} icon={DollarSign} />
            ) : null}
          </>
        }
      />
    </View>
  );
}

export default function EndometrialBiopsyFactsheetScreen({
  navigation,
  route,
}: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [activeTab, setActiveTab] = useState<BiopsyTab>("overview");
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);
  const handleBack = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Procedures",
  });
  const procedure = useMemo(
    () => getFactSheetRouteBlock("procedure", route.params.procedureId),
    [route.params.procedureId],
  );
  const relatedTests = useMemo(() => {
    if (!procedure) return [];

    return getRelatedBlocks(procedure.id, "related-to")
      .filter(
        ({ relationship }) =>
          relationship.from === procedure.id &&
          relationship.label === "Can lead to",
      )
      .map(({ block }) => block)
      .filter((block): block is ContentBlock<"test"> => block.kind === "test");
  }, [procedure]);

  if (!procedure || procedure.kind !== "procedure") return null;

  const body = procedure.body;
  const fr = locale === "fr" ? body.frBody : undefined;
  const localized = {
    procedureName: fr?.procedureName ?? body.procedureName,
    description: fr?.description ?? body.description,
    howItWorks: fr?.howItWorks ?? body.howItWorks,
    duration: fr?.duration ?? body.duration,
    recoveryTime: fr?.recoveryTime ?? body.recoveryTime,
    painAndComfort: fr?.painAndComfort ?? body.painAndComfort ?? [],
    risks: fr?.risks ?? body.risks ?? [],
    prepInstructions: fr?.prepInstructions ?? body.prepInstructions ?? [],
    whatToExpect: fr?.whatToExpect ?? body.whatToExpect ?? [],
  } satisfies Partial<ProcedureBody>;
  const tabs: FactSheetTab<BiopsyTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "procedure",
      label: t("procedure_detail.tab_procedure"),
      shortLabel: t("procedure_detail.tab_procedure_short"),
      icon: Wrench,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  const quickFacts = [
    {
      label: t("learn.test_detail.quick_fact_sample"),
      value: t("procedure_detail.sample_endometrial_tissue"),
    },
    ...(localized.duration
      ? [
          {
            label: t("learn.test_detail.quick_fact_time"),
            value: localized.duration,
          },
        ]
      : []),
    {
      label: t("learn.test_detail.quick_fact_anesthesia"),
      value:
        body.anesthesia === "none"
          ? t("procedure_detail.anesthesia_none")
          : (body.anesthesia ?? t("procedure_detail.anesthesia_none")),
    },
    ...(localized.recoveryTime
      ? [
          {
            label: t("procedure_detail.recovery_label"),
            value: localized.recoveryTime,
          },
        ]
      : []),
  ];

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            ...GLASS_CARD_STYLE,
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 32,
          }}
        >
          <View style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
            <IconButton
              onPress={handleBack}
              hitSlop={8}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.05)"
              pressedBackgroundColor="rgba(0,0,0,0.05)"
              pressedOpacity={1}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            >
              <X size={16} color={EMBER.colors.textLight} />
            </IconButton>
          </View>

          <FactSheetHeader
            title={localized.procedureName}
            subtitle={procedure.subtitle}
            emoji="🧪"
            badges={[{ label: t("learn.test_detail.category_biopsy") }]}
          />

          <FactSheetTabControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Heart}>
                  {t("procedure_detail.section_sample_purpose")}
                </SectionTitle>
                <BodyCopy>{localized.description}</BodyCopy>
              </View>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Info}>
                  {t("access.testing.comparison.at_a_glance")}
                </SectionTitle>
                <CompactValueGrid items={quickFacts} />
              </View>
              {localized.painAndComfort.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("procedure_detail.section_pain_and_comfort")}
                  </SectionTitle>
                  <BulletList items={localized.painAndComfort} />
                </View>
              ) : null}
              {localized.prepInstructions.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={ClipboardCheck}>
                    {t("procedure_detail.section_how_to_prepare")}
                  </SectionTitle>
                  <BulletList items={localized.prepInstructions} />
                </View>
              ) : null}
              {localized.whatToExpect.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("procedure_detail.section_what_to_expect")}
                  </SectionTitle>
                  <BulletList items={localized.whatToExpect} />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "procedure" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Wrench}>
                  {t("procedure_detail.section_how_it_works")}
                </SectionTitle>
                <BodyCopy>{localized.howItWorks}</BodyCopy>
              </View>
              {localized.risks.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertTriangle}>
                    {t("procedure_detail.section_risks")}
                  </SectionTitle>
                  <BulletList items={localized.risks} />
                </View>
              ) : null}
              {procedure.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("procedure_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={procedure.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 16 }}>
              <BodyCopy>
                {t("procedure_detail.tests_using_sample_description")}
              </BodyCopy>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {relatedTests.map((test) => (
                  <TestCategoryCard
                    key={test.id}
                    test={test}
                    onPress={() =>
                      navigation.navigate("TestDetail", {
                        testId: String(test.id),
                      })
                    }
                  />
                ))}
              </View>
            </View>
          ) : null}

          <View
            style={{
              marginTop: 32,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                lineHeight: 17,
                textAlign: "center",
              }}
            >
              {t("procedure_detail.disclaimer")}
            </Text>
          </View>
        </View>
      </ScrollView>
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
